/**
 * Edit Permissions Modal — edit an existing admin user's permissions.
 * Uses the shared, accessible <PermissionEditor /> (same surface as Add).
 * Adds: inline save-error handling (modal stays open), reset, and a confirmation
 * when a save would remove access from a large share of currently-granted resources.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  Typography,
  Box,
  IconButton,
  Alert,
  CircularProgress,
  Avatar,
} from '@mui/material';
import {
  Close as CloseIcon,
  Save as SaveIcon,
  RestartAlt as ResetIcon,
} from '@mui/icons-material';
import { AdminUser, Permission } from '../../types/auth';
import PermissionEditor, {
  PermissionMap,
  permissionsArrayToMap,
  mapToPermissionsArray,
} from './PermissionEditor';

interface EditPermissionsModalProps {
  open: boolean;
  onClose: () => void;
  user: AdminUser | null;
  onSave: (userId: string, permissions: Permission[]) => Promise<void>;
}

export const EditPermissionsModal: React.FC<EditPermissionsModalProps> = ({ open, onClose, user, onSave }) => {
  const [permissions, setPermissions] = useState<PermissionMap>({});
  const [originalMap, setOriginalMap] = useState<PermissionMap>({});
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (open && user) {
      const map = permissionsArrayToMap(user.permissions);
      setPermissions(map);
      setOriginalMap(map);
      setError(null);
    }
  }, [open, user]);

  // Destructive-change detection: how many originally-granted resources are now No Access
  const { grantedBefore, removedCount, isDestructive } = useMemo(() => {
    const grantedIds = Object.entries(originalMap).filter(([, v]) => v !== 'none').map(([id]) => id);
    const removed = grantedIds.filter((id) => (permissions[id] ?? 'none') === 'none');
    return {
      grantedBefore: grantedIds.length,
      removedCount: removed.length,
      isDestructive: grantedIds.length > 0 && removed.length / grantedIds.length > 0.25,
    };
  }, [originalMap, permissions]);

  const doSave = async () => {
    if (!user) return;
    setConfirmOpen(false);
    setIsSaving(true);
    setError(null);
    try {
      await onSave(user.id, mapToPermissionsArray(permissions));
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save permissions');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveClick = () => {
    if (isDestructive) setConfirmOpen(true);
    else doSave();
  };

  if (!user) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: '14px' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar src={user.avatar_url} sx={{ width: 40, height: 40 }}>
            {user.name?.[0] || user.email?.[0]}
          </Avatar>
          <Box>
            <Typography variant="h6">Edit Permissions</Typography>
            <Typography variant="body2" color="text.secondary">{user.name} ({user.email})</Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2.5 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
        )}
        <PermissionEditor value={permissions} onChange={setPermissions} />
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button startIcon={<ResetIcon />} onClick={() => setPermissions(originalMap)} disabled={isSaving}>
          Reset
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} disabled={isSaving}>Cancel</Button>
        <Button
          variant="contained"
          startIcon={isSaving ? <CircularProgress size={18} /> : <SaveIcon />}
          onClick={handleSaveClick}
          disabled={isSaving}
        >
          {isSaving ? 'Saving...' : 'Save Permissions'}
        </Button>
      </DialogActions>

      {/* Destructive-change confirmation */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Remove access from {removedCount} resource{removedCount === 1 ? '' : 's'}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This change removes <strong>{user.name || user.email}</strong>'s access to {removedCount} of{' '}
            {grantedBefore} previously-granted resources. They will lose those sections on next login.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Keep editing</Button>
          <Button color="error" variant="contained" onClick={doSave}>Remove &amp; save</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default EditPermissionsModal;
